// Web-specific implementation of DevAgeLineChart using Recharts.
// Vite resolves .web.tsx before .tsx, so this file is used on web and the
// react-native-svg version (DevAgeLineChart.tsx) is used on mobile.
import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export interface DevAgeDataPoint {
  physicalAge: number;
  [domain: string]: number | null | undefined;
}

interface DevAgeLineChartProps {
  data: DevAgeDataPoint[];
}

const DOMAIN_ORDER = ['communication', 'motor', 'finemotor', 'grossmotor', 'cognitive', 'socialemotional'];

const DOMAIN_COLORS: Record<string, string> = {
  communication:   '#2563EB',  // vivid blue
  motor:           '#7C3AED',  // violet (distinct from blue)
  finemotor:       '#DC2626',  // vivid red
  grossmotor:      '#EA580C',  // vivid orange
  cognitive:       '#16A34A',  // forest green
  socialemotional: '#DB2777',  // hot pink / magenta (distinct from green)
};

const DOMAIN_LABELS: Record<string, string> = {
  communication:   'Communication',
  motor:           'Motor',
  finemotor:       'Fine Motor',
  grossmotor:      'Gross Motor',
  cognitive:       'Cognitive',
  socialemotional: 'Social-Emotional',
};

function formatAge(months: number): string {
  if (months < 24) return `${Math.round(months)}mo`;
  const wholeYears = Math.floor(months / 12);
  const remMonths = Math.round(months % 12);
  return remMonths > 0 ? `${wholeYears}y ${remMonths}mo` : `${wholeYears}y`;
}

export function DevAgeLineChart({ data }: DevAgeLineChartProps) {
  const sorted = [...data].sort((a, b) => a.physicalAge - b.physicalAge);

  // Find which domains have at least one data point, in canonical order
  const activeDomains = DOMAIN_ORDER.filter(domain =>
    sorted.some(p => p[domain] != null)
  );

  if (sorted.length < 2 || activeDomains.length === 0) {
    return (
      <div style={{ height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#aaa', fontSize: 13 }}>Not enough data to display chart</span>
      </div>
    );
  }

  const xValues = sorted.map(d => d.physicalAge);
  const xMax = Math.max(...xValues);
  const xPad = xMax * 0.04 || 1;

  // Collect all dev age values for Y domain
  const allDevAges: number[] = [];
  for (const pt of sorted) {
    for (const domain of activeDomains) {
      const v = pt[domain];
      if (v != null) allDevAges.push(v as number);
    }
  }
  const yMax = Math.ceil(Math.max(...allDevAges) * 1.06);

  // Shared axis floor: youngest of any physical or dev age, minus 1 month, floored at 0
  const allAges = [...xValues, ...allDevAges];
  const axisMin = Math.max(0, Math.floor(Math.min(...allAges)) - 1);

  // The x=y reference line spans from axis floor to whichever axis max is larger
  const maxDomain = Math.max(xMax + xPad, yMax);

  return (
    <div style={{ marginLeft: 2, marginRight: 2 }}>
      {/* Custom legend rendered outside ResponsiveContainer so it wraps freely
          without competing with the chart area. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', paddingBottom: 14, paddingLeft: 20 }}>
        {activeDomains.map(domain => (
          <div key={domain} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: DOMAIN_COLORS[domain], flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: '#555' }}>{DOMAIN_LABELS[domain]}</span>
          </div>
        ))}
        {/* Reference line legend entry */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 18, height: 2, background: 'repeating-linear-gradient(to right, #bbb 0px, #bbb 4px, transparent 4px, transparent 8px)', flexShrink: 0 }} />
          <span style={{ fontSize: 11, color: '#aaa' }}>Typical (age = dev age)</span>
        </div>
      </div>

      {/* Outer flex row: CSS-rotated Y label + chart */}
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        <div style={{
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
          fontSize: 10,
          color: '#aaa',
          textAlign: 'center',
          flexShrink: 0,
          width: 16,
        }}>
          Dev Age
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height={372}>
            <LineChart data={sorted} margin={{ top: 8, right: 24, bottom: 36, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="physicalAge"
                type="number"
                scale="linear"
                domain={[axisMin, xMax + xPad]}
                tickFormatter={formatAge}
                tick={{ fontSize: 10, fill: '#888' }}
                tickCount={6}
                label={{ value: 'Physical Age', position: 'insideBottom', offset: -12, fontSize: 10, fill: '#aaa' }}
              />
              <YAxis
                tickFormatter={formatAge}
                tick={{ fontSize: 10, fill: '#666' }}
                width={44}
                domain={[axisMin, yMax]}
              />
              <Tooltip
                formatter={((value: any, name: any) => [`${value}mo`, String(name)]) as any}
                labelFormatter={(label: any) => `Physical age: ${formatAge(Number(label))}`}
              />
              {/* Dashed x=y reference: where dev age equals physical age (typical development) */}
              <Line
                data={[{ physicalAge: axisMin, refLine: axisMin }, { physicalAge: maxDomain, refLine: maxDomain }]}
                type="linear"
                dataKey="refLine"
                stroke="#ccc"
                strokeDasharray="5 5"
                strokeWidth={1.5}
                dot={false}
                legendType="none"
                isAnimationActive={false}
              />
              {activeDomains.map(domain => (
                <Line
                  key={domain}
                  type="monotone"
                  dataKey={domain}
                  name={DOMAIN_LABELS[domain] ?? domain}
                  stroke={DOMAIN_COLORS[domain]}
                  strokeWidth={2}
                  dot={{ r: 4, fill: DOMAIN_COLORS[domain], stroke: '#fff', strokeWidth: 1.5 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
